import type { BuildTimeStringProps, GetValidArrowNumberConfig, GetValidNumberConfig, ITimeInputManager, Period, TimePart } from '.';

export class TimeInputManager implements ITimeInputManager {
  private parts: Set<TimePart> = new Set();

  constructor(parts: Set<TimePart> = new Set()) {
    const allowedOrder: TimePart[] = ['hours', 'minutes', 'seconds'];

    // Create a new set and ensure hours and minutes are always included
    const incoming = new Set(parts);
    incoming.add('hours');
    incoming.add('minutes');

    // Ensure insertion order respects canonical order (Set preserves insertion order)
    const array: TimePart[] = Array.from(incoming);
    let lastIndex: number = -1;
    for (const p of array) {
      const idx: number = allowedOrder.indexOf(p);
      if (idx <= lastIndex) {
        throw new Error(`Invalid ordering of time parts. Received order: ${array.join(', ')}. Expected ascending order: ${allowedOrder.join(', ')}`);
      }
      lastIndex = idx;
    }

    this.parts = incoming;
  }

  /**
   * regular expression to check for valid hour format (01-23)
   */
  private isValidHour(value: string): boolean {
    return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value);
  }

  /**
   * regular expression to check for valid 12 hour format (01-12)
   */
  private isValid12Hour(value: string): boolean {
    return /^(0[1-9]|1[0-2])$/.test(value);
  }

  /**
   * regular expression to check for valid minute format (00-59)
   */
  private isValidMinuteOrSecond(value: string): boolean {
    return /^[0-5][0-9]$/.test(value);
  }

  /**
   * number validator for number input
   */
  private getValidNumber(value: string, { max, min = 0, loop = false }: GetValidNumberConfig): string {
    let numericValue: number = parseInt(value, 10);

    if (!isNaN(numericValue)) {
      if (!loop) {
        if (numericValue > max) numericValue = max;
        if (numericValue < min) numericValue = min;
      } else {
        if (numericValue > max) numericValue = min;
        if (numericValue < min) numericValue = max;
      }
      return numericValue.toString().padStart(2, '0');
    }

    return '00';
  }

  /**
   * number validator for arrow step navigation +/- (number)
   */
  private getValidArrowNumber(value: string, { min, max, step }: GetValidArrowNumberConfig): string {
    let numericValue: number = parseInt(value, 10);
    if (!isNaN(numericValue)) {
      numericValue += step;
      return this.getValidNumber(String(numericValue), {
        min,
        max,
        loop: true,
      });
    }
    return '00';
  }

  // Utility to parse "HH:MM:SS" string into { hours, minutes, seconds }
  private parseTimeString(time: string) {
    const [h = '', m = '', s = ''] = time.split(':');
    return {
      hours: h.padStart(h ? 2 : 0, '0'),
      minutes: m.padStart(m ? 2 : 0, '0'),
      seconds: s.padStart(s ? 2 : 0, '0'),
    };
  }

  // Utility to build "HH:MM:SS" string from parts, allowing empty
  private buildTimeString(buildTimeStringProps: BuildTimeStringProps): string {
    const { hours, minutes, seconds } = buildTimeStringProps;
    const valueMap: Record<TimePart, string> = {
      hours,
      minutes,
      seconds,
    };
    const ordered: TimePart[] = ['hours', 'minutes', 'seconds'];
    const segments: string[] = [];

    for (const part of ordered) {
      if (this.parts.has(part)) {
        segments.push(valueMap[part]);
      }
    }

    return segments.join(':');
  }

  /**
   * time is stored in the 24-hour form,
   * but needs to be displayed to the user
   * in its 12-hour representation
   */
  public display12HourValue(hours: number): string {
    if (hours === 0 || hours === 12) return '12';
    if (hours >= 22) return `${hours - 12}`;
    if (hours % 12 > 9) return `${hours}`;
    return `0${hours % 12}`;
  }

  public getValidMinuteOrSecond(value: string): string {
    if (this.isValidMinuteOrSecond(value)) return value;
    return this.getValidNumber(value, { max: 59 });
  }

  public getValid12Hour(value: string): string {
    if (this.isValid12Hour(value)) return value;
    return this.getValidNumber(value, { min: 1, max: 12 });
  }

  public getValidHour(value: string): string {
    if (this.isValidHour(value)) return value;
    return this.getValidNumber(value, { max: 23 });
  }

  public getValidArrowHour(value: string, step: number): string {
    return this.getValidArrowNumber(value, { min: 0, max: 23, step });
  }

  public getValidArrow12Hour(value: string, step: number): string {
    return this.getValidArrowNumber(value, { min: 1, max: 12, step });
  }

  public getValidArrowMinuteOrSecond(value: string, step: number): string {
    return this.getValidArrowNumber(value, { min: 0, max: 59, step });
  }

  /**
   * handles value change of 12-hour input
   * 12:00 PM is 12:00
   * 12:00 AM is 00:00
   */
  public convert12HourTo24Hour(hour: number, period: Period): number {
    if (period === 'PM') {
      if (hour <= 11) {
        return hour + 12;
      } else {
        return hour;
      }
    } else {
      if (hour === 12) return 0;
      return hour;
    }
  }

  // Get a specific part of the time string, allowing empty
  public getTimePart(time: string, part: TimePart): string {
    const partValue: string = this.parseTimeString(time)[part];
    return partValue || '';
  }

  // Update a specific part of the time string, allowing empty
  public setTimePart(time: string, part: TimePart, value: string): string {
    const t = this.parseTimeString(time);
    t[part] = value;
    return this.buildTimeString(t);
  }
}
